import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { ChevronRight, EyeIcon, GlobeIcon } from 'lucide-react';

import { DrawerPopup } from 'components/UI/DrawerPopup';
import { font, space } from 'settings/tokens';

interface ViewChoiceSheetProps {
  open: boolean;
  /** Chose their own view — also what DISMISSING does. */
  onChooseOwner: () => void;
  /** Chose the visitor's view (`OWNER_PUBLIC_PREVIEW`). */
  onChooseVisitor: () => void;
}

/**
 * VRG-781 — asked of a signed-in owner, on every scan of their own tag: which view?
 *
 * ## Why it asks EVERY time rather than remembering
 *
 * The answer is a function of the SITUATION, not a preference. An owner alternates
 * unpredictably between checking their own item and showing it to someone, so any
 * remembered answer is wrong roughly half the time BY CONSTRUCTION — remembering is the
 * wrong instrument for a value that is not stable.
 *
 * The asymmetry settles it. Landing in owner view while handing the phone to a buyer
 * puts the owner's name, their provenance and a Transfer button in front of that buyer,
 * and the owner fumbles for the menu while they watch. Being asked costs one tap and
 * leaks nothing. Habituation is the accepted cost (working-conventions §43's
 * modal-fatigue argument is about FREQUENT gates; a scan is not frequent).
 *
 * ## Why the labels never say "public"
 *
 * "View as public" can read as *make this public* to an owner holding their item out to
 * a buyer, and a sheet that might be misread as a privacy switch is worse than no sheet.
 * So each option is named by what it CONTAINS, and the second line is the whole
 * difference between them rather than a restatement of the label.
 *
 * ## Dismissing is never a dead end
 *
 * X, the backdrop and a swipe all land on the owner view. A sheet between the tap and
 * the record must always have a way through — and the owner view is the right default
 * because it is the one the router would have chosen anyway.
 */
export const ViewChoiceSheet = ({
  open,
  onChooseOwner,
  onChooseVisitor,
}: ViewChoiceSheetProps) => (
  <DrawerPopup
    title='Open as'
    handleOpen={open}
    handleClose={onChooseOwner}
    child={
      <Stack spacing={2} sx={{ pb: 1 }}>
        <Option
          icon={<EyeIcon size={22} strokeWidth={1.5} />}
          label='Your view'
          detail='Everything on this certificate, including your ownership details, its provenance and recent activity.'
          onClick={onChooseOwner}
        />
        <Option
          icon={<GlobeIcon size={22} strokeWidth={1.5} />}
          label='What visitors see'
          detail='The certificate as someone else scanning this tag sees it. Nothing changes — you can switch back at any time.'
          onClick={onChooseVisitor}
        />
      </Stack>
    }
  />
);

/**
 * An ACTION row, so it takes the action-row hover treatment (working-conventions §34):
 * brand border plus the focus ring, no persistent selected state — clicking it does
 * something and the sheet closes, rather than leaving a choice visibly selected.
 */
const Option = ({
  icon,
  label,
  detail,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  detail: string;
  onClick: () => void;
}) => (
  <Box
    component='button'
    type='button'
    onClick={onClick}
    sx={theme => ({
      display: 'flex',
      alignItems: 'flex-start',
      gap: `${space.md}px`,
      width: '100%',
      textAlign: 'left',
      p: 2,
      border: `1px solid ${theme.palette.primary[100] ?? theme.palette.divider}`,
      borderRadius: '12px',
      backgroundColor: 'background.paper',
      cursor: 'pointer',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      '&:hover, &:focus-visible': {
        borderColor: theme.palette.brand?.[600] ?? theme.palette.primary.main,
        boxShadow: `0 0 0 3px ${theme.palette.brand?.['600-6%'] ?? 'transparent'}`,
        outline: 'none',
      },
    })}
  >
    <Box sx={{ mt: '2px', flexShrink: 0, display: 'flex' }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: font.body, fontWeight: 500 }}>{label}</Typography>
      {/* 1.5 rather than the body default: this is the line carrying the actual
          difference between the two options, so it has to be comfortable to read
          (working-conventions §15 — descriptive copy in a modal gets breathing room). */}
      <Typography
        sx={{ fontSize: font.label, color: 'text.secondary', lineHeight: 1.5, mt: '2px' }}
      >
        {detail}
      </Typography>
    </Box>
    <Box sx={{ mt: '2px', flexShrink: 0, display: 'flex', color: 'text.secondary' }}>
      <ChevronRight size={18} strokeWidth={1.5} />
    </Box>
  </Box>
);

export default ViewChoiceSheet;
